import React, { useState, useEffect } from 'react';
// Certificate component with automatic generation on 404 errors
// When the certificate API returns 404, it automatically calls the generate API
// and hides the manual generate button during this process
// 
// Mobile Download Support:
// - Uses Cordova file API when available for native device saving
// - Falls back to Web Share API for mobile sharing
// - Opens certificate in new tab for mobile devices without download support
// - Provides fallback download methods for various mobile scenarios
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { Configuration } from '../../../assets/data/config';
import { apiServices } from '../../../services/apiServices';

import jsPDF from "jspdf";


const Certificate = () => {
  // Get user data from Redux store
  const { user, isLoading } = useSelector((state) => state.auth);
  // menuAnchorEl state removed - not needed for simplified UI
  const [isGenerating, setIsGenerating] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  // showMobile and showEmail state removed - not needed for API certificate

  // New state for certificate from API
  const [certificateImage, setCertificateImage] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState(null);
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  
  // State for share preview modal
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [sharePreviewMessage, setSharePreviewMessage] = useState('');

  // Helper function to check if download attribute is supported
  const isDownloadSupported = () => {
    const link = document.createElement('a');
    return 'download' in link;
  };

  // Helper function to detect iOS devices
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Helper function to detect Safari browser
  const isSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  const buildFilename = () => {
    const safeName = (userData.name || 'User')
      .toString()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters but keep spaces
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    
    const idNumber = userData.idNumber || 'IN-0000000-PK';
    
    return `Certificate-${safeName}-${idNumber}.png`;
  };

  // iOS-specific PDF download handler - Force save to device
  const handleIOSPDFDownload = (blob, filename) => {
    try {
      console.log('Handling iOS PDF download for Certificate:', filename);
      setShareStatus('Preparing PDF download for iOS...');

      // For iOS PDFs, we need to force save to device, not open in browser
      // Try multiple approaches in sequence
      
      // Approach 1: Try Web Share API first (most reliable for iOS)
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], filename, { type: 'application/pdf' });
          if (navigator.canShare({ files: [file] })) {
            navigator.share({
              files: [file],
              title: 'I Am Peacekeeper Certificate PDF',
              text: `Save your certificate PDF: ${filename}`
            }).then(() => {
              setShareStatus('PDF shared successfully! Choose "Save to Files" to save to your device.');
            }).catch((shareError) => {
              console.log('Web Share failed, trying alternative method:', shareError);
              handleIOSPDFFallback(blob, filename);
            });
            return;
          }
        } catch (shareError) {
          console.log('Web Share API error, trying fallback:', shareError);
          handleIOSPDFFallback(blob, filename);
          return;
        }
      }

      // If Web Share is not available, use fallback
      handleIOSPDFFallback(blob, filename);

    } catch (error) {
      console.error('iOS PDF download failed:', error);
      handleIOSPDFFallback(blob, filename);
    }
  };

  // iOS PDF fallback method - More aggressive approach
  const handleIOSPDFFallback = (blob, filename) => {
    try {
      console.log('Using iOS PDF fallback method for Certificate:', filename);
      setShareStatus('Preparing PDF for device save...');

      // Create a new blob with explicit PDF MIME type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      
      // Try to use Cordova file system if available
      if (window.cordova && window.cordova.file) {
        console.log('Using Cordova file system for PDF save...');
        setShareStatus('Saving PDF to device using Cordova...');
        
        saveFileToDevice(pdfBlob, filename).then((filePath) => {
          console.log('PDF saved successfully to:', filePath);
          setShareStatus(`PDF saved to device: ${filename}`);
          
          // Show toast notification if available
          if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
            window.cordova.plugins.toast.show(`PDF saved: ${filename}`, 'long', 'bottom');
          }
        }).catch((error) => {
          console.error('Cordova save failed, trying web fallback:', error);
          handleIOSPDFWebFallback(pdfBlob, filename);
        });
        return;
      }

      // Web fallback for non-Cordova environments
      handleIOSPDFWebFallback(pdfBlob, filename);

    } catch (error) {
      console.error('iOS PDF fallback failed:', error);
      setShareStatus('PDF save failed. Please try the Web Share option.');
    }
  };

  // iOS-specific download handler for images
  const handleIOSDownload = (blob, filename) => {
    try {
      console.log('Handling iOS download for Certificate:', filename);
      setShareStatus('Preparing download for iOS...');

      // Determine file type for proper MIME type
      const isPDF = filename.toLowerCase().endsWith('.pdf');
      const mimeType = isPDF ? 'application/pdf' : 'image/png';

      // For iOS, we need to use a different approach
      if (navigator.share && navigator.canShare) {
        // Try Web Share API first (iOS 13+)
        try {
          const file = new File([blob], filename, { type: mimeType });
          
          if (navigator.canShare({ files: [file] })) {
            const shareTitle = isPDF ? 'I Am Peacekeeper Certificate PDF' : 'I Am Peacekeeper Certificate';
            const shareText = isPDF ? `Download your certificate PDF: ${filename}` : `Download your certificate: ${filename}`;
            
            navigator.share({
              files: [file],
              title: shareTitle,
              text: shareText
            }).then(() => {
              setShareStatus('File shared successfully! Check your chosen app.');
            }).catch((error) => {
              console.log('Web Share failed, trying alternative method:', error);
              handleIOSFallbackDownload(blob, filename, isPDF);
            });
            return;
          }
        } catch (shareError) {
          console.log('Web Share API error, using fallback:', shareError);
          handleIOSFallbackDownload(blob, filename, isPDF);
          return;
        }
      }

      // Fallback for older iOS versions or if Web Share fails
      handleIOSFallbackDownload(blob, filename, isPDF);

    } catch (error) {
      console.error('iOS download failed:', error);
      handleIOSFallbackDownload(blob, filename, filename.toLowerCase().endsWith('.pdf'));
    }
  };

  // iOS fallback download method
  const handleIOSFallbackDownload = (blob, filename, isPDF = false) => {
    try {
      console.log('Using iOS fallback download method for Certificate:', filename, 'isPDF:', isPDF);
      setShareStatus('Opening file for iOS download...');

      // For PDFs on iOS, we need a different approach to prevent opening in the app
      if (isPDF) {
        // Create a blob URL instead of data URL for PDFs to avoid opening in browser
        const blobUrl = URL.createObjectURL(blob);
        
        // Create a temporary link element with proper attributes
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add target="_blank" to prevent opening in current tab
        link.target = '_blank';
        
        // Add additional attributes to force download on iOS
        link.setAttribute('rel', 'noopener');
        link.setAttribute('type', 'application/pdf');
        
        // Add to DOM
        document.body.appendChild(link);
        
        // Trigger download with user gesture
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        
        // For iOS, try multiple approaches to ensure download
        try {
          link.dispatchEvent(clickEvent);
          
          // Additional iOS-specific approach: try programmatic click
          setTimeout(() => {
            if (link.click) {
              link.click();
            }
          }, 100);
          
        } catch (clickError) {
          console.log('Click event failed, trying alternative:', clickError);
          // Fallback: try direct click
          if (link.click) {
            link.click();
          }
        }
        
        // Clean up blob URL after a delay
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(blobUrl);
        }, 3000);

        setShareStatus('PDF download initiated. Check your Files app or Downloads folder.');
        
        // Show specific instructions for PDF downloads
        setTimeout(() => {
          setShareStatus('If PDF didn\'t download automatically, try tapping the download button again or check your Files app.');
        }, 4000);

        return;
      }

      // For images, use the original data URL approach
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add to DOM and trigger download
        document.body.appendChild(link);
        
        // For iOS, we need to trigger the click in a user gesture
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        
        link.dispatchEvent(clickEvent);
        
        // Clean up
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 1000);

        setShareStatus('Download initiated. Check your Downloads folder.');
        
        // Show instructions for iOS users
        setTimeout(() => {
          setShareStatus('If download didn\'t start, long press the image above and select "Save to Photos" or "Add to Files"');
        }, 2000);

        // Additional fallback: try opening in new tab for iOS
        setTimeout(() => {
          const newWindow = window.open(dataUrl, '_blank');
          if (newWindow) {
            setShareStatus('File opened in new tab. Long press and save on iOS.');
          }
        }, 3000);
      };
      
      reader.onerror = (error) => {
        console.error('Error reading blob for iOS:', error);
        setShareStatus('Failed to prepare file for download. Please try again.');
      };
      
      reader.readAsDataURL(blob);

    } catch (error) {
      console.error('iOS fallback download failed:', error);
      setShareStatus('Download failed. Please try saving the file manually.');
    }
  };

  // iOS PDF web fallback - Force download without opening
  const handleIOSPDFWebFallback = (pdfBlob, filename) => {
    try {
      console.log('Using iOS PDF web fallback for Certificate:', filename);
      setShareStatus('Preparing PDF download...');

      // Create a data URL instead of blob URL to force download
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        
        // Create a temporary link element with aggressive download settings
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add attributes to force download
        link.setAttribute('download', filename);
        link.setAttribute('type', 'application/pdf');
        link.setAttribute('rel', 'noopener');
        
        // Add to DOM
        document.body.appendChild(link);
        
        // Try multiple click approaches
        try {
          // Method 1: Direct click
          link.click();
          
          // Method 2: Synthetic click event
          setTimeout(() => {
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            link.dispatchEvent(clickEvent);
          }, 50);
          
          // Method 3: Programmatic click as last resort
          setTimeout(() => {
            if (link.click) {
              link.click();
            }
          }, 100);
          
          // Method 4: Try iframe approach for iOS (more aggressive)
          setTimeout(() => {
            try {
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = dataUrl;
              document.body.appendChild(iframe);
              
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              }, 1000);
            } catch (iframeError) {
              console.log('Iframe method failed:', iframeError);
            }
          }, 200);
          
        } catch (clickError) {
          console.log('Click methods failed:', clickError);
        }
        
        // Clean up
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 2000);

        setShareStatus('PDF download initiated. Check your Downloads or Files app.');
        
        // Show instructions for manual saving if needed
        setTimeout(() => {
          setShareStatus('If PDF opened in browser, long press the PDF and select "Save to Files" or "Add to Files".');
        }, 2000);
      };
      
      reader.onerror = (error) => {
        console.error('Error reading PDF blob:', error);
        setShareStatus('Failed to prepare PDF for download. Please try again.');
      };
      
      reader.readAsDataURL(pdfBlob);

    } catch (error) {
      console.error('iOS PDF web fallback failed:', error);
      setShareStatus('PDF download failed. Please try the Web Share option above.');
    }
  };

  // User data for sharing messages
  const userData = {
    name: user?.username || user?.name || 'User Name',
    idNumber: user?.peacekeeper_id || 'IN-0000000-PK'
  };

  // Generate QR code data - registration URL based on usertype and temp_peacekeeper_id
  const generateQRCodeData = () => {
    const domain = Configuration.frontendDomain;
    const peacekeeperId = user?.peacekeeper_id || 'IN-0000000-PK';

    // Priority 1: For Entity usertype with categoryname
    if (user?.usertype === 'Entity' && user?.categoryname) {
      const cleanName = (user.name || user.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = user.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    if (user?.temp_peacekeeper_id) {
      return `${domain}/register?${user.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users
    return `${domain}/register?${peacekeeperId}`;
  };

  const qrCodeData = generateQRCodeData();

  // Menu handling functions removed - not needed for simplified UI

  // Manual certificate generation function
  const handleGenerateCertificate = async () => {
    try {
      setIsGenerating(true);
      setShareStatus('Generating new certificate...');

      // Call the PUT endpoint to generate/update the certificate
      const updateResponse = await apiServices.putAPI(`update-certificate/${userData.idNumber}`, {
        forceRegenerate: true
      });

      console.log('Certificate generation response:', updateResponse);

      if (updateResponse.status === 200) {
        setShareStatus('Certificate generated successfully! Fetching...');

        // Wait a moment for the certificate to be generated, then fetch it
        setTimeout(async () => {
          try {
            const retryResponse = await apiServices.getBlobAPI(`certificate/${user.userid}`);

            if (retryResponse.data) {
              const blob = new Blob([retryResponse.data], { type: 'image/png' });
              const imageUrl = URL.createObjectURL(blob);
              setCertificateImage(imageUrl);
              setShareStatus('Certificate generated and loaded successfully!');
              console.log('Certificate fetched after generation:', imageUrl);
            }
          } catch (retryError) {
            console.error('Error fetching certificate after generation:', retryError);
            setShareStatus('Certificate was generated but failed to load. Please refresh the page.');
          }
        }, 2000); // Wait 2 seconds for generation to complete

      } else {
        throw new Error('Failed to generate certificate');
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      setShareStatus('Failed to generate certificate. Please try again later.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate referral link based on usertype and temp_peacekeeper_id (same logic as SendInvitation)
  const generateReferralLink = () => {
    if (!user) return '';

    const domain = Configuration.frontendDomain;
    const peacekeeperId = user.peacekeeper_id || 'IN-0000000-PK-IAPM';

    // Priority 1: For Entity usertype with categoryname, include category in URL
    // Format: domain/register?Name-Category-PeacekeeperID
    if (user.usertype === 'Entity' && user.categoryname) {
      const cleanName = (user.name || user.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = user.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    // Format: domain/register?{temp_peacekeeper_id}
    if (user.temp_peacekeeper_id) {
      return `${domain}/register?${user.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users (no entity, no temp ID)
    // Format: domain/register?referencenumber={peacekeeper_id}
    return `${Configuration.frontendDomain}/register?${user?.username?.replace(/\s+/g, '-') || 'User'}-${user?.peacekeeper_id || 'IN-0000000-PK-IAPM'}`;
  };

  // Open the share preview modal
  const handleShareNoDownload = () => {
    try {
      if (certificateImage) {
        const referralLink = generateReferralLink();
        const message = `📲 Join the I Am Peacekeeper Movement

Warm greetings,🌟

I invite you to become an I Am Peacekeeper, a vibrant movement for Global Justice, Love & Peace 🌍✨

Scan my bespoke 🖼️ QR Code or tap this 🔗 secure link to register with my reference number:

${referralLink}

7 REASONS TO BECOME I AM PEACEKEEPER

1. 🌍 To further the CAUSE of Global Justice, Love & Peace

2. 🤝 To make friends through SOCIAL NETWORKING

3. 🚀 To improve your career & business prospects through PROFESSIONAL NETWORKING

4. 🎁 To earn PEACE INCENTIVES when raising money for the movement

5. 🧘 To learn the secrets of INNER PEACE

6. 🏛️ To participate in EVENTS with inspiring leaders

7. 🌟 To feel FULFILLED with a higher purpose

Your presence will add immense value. I look forward to welcoming you 🌈💫

With respect,

I Am Peacekeeper | ${userData.name}

ID: ${userData.idNumber}`;
        
        setSharePreviewMessage(message);
        setShowSharePreview(true);
      } else {
        setShareStatus('No certificate image available to share.');
      }
    } catch (error) {
      console.error('Error preparing share preview:', error);
      setShareStatus('Failed to prepare share preview. Please try again.');
    }
  };

  // Actually send the share after preview
  const handleSendShare = async () => {
    try {
      setShowSharePreview(false);
      setIsGenerating(true);
      setShareStatus('Preparing to share...');
      
      // Convert the certificate image to blob for sharing
      const response = await fetch(certificateImage);
      const blob = await response.blob();
      const filename = buildFilename();

      if (window.cordova) {
        await shareImageToWhatsApp(blob, filename, sharePreviewMessage);
        setShareStatus('Certificate shared successfully!');
        setIsGenerating(false);
        return;
      }

      try {
        const file = new File([blob], filename, { type: 'image/png' });

        // Try Web Share API with file attachment
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({
            files: [file],
            title: 'Peacekeeper Certificate',
            text: sharePreviewMessage
          }).then(() => {
            setShareStatus('Certificate shared with attachment via Web Share!');
          }).catch(() => {
            // If Web Share fails, try base64 sharing
            shareWithBase64(blob, filename, sharePreviewMessage);
          });
        } else {
          // Web Share not available, try base64 sharing
          shareWithBase64(blob, filename, sharePreviewMessage);
        }

      } catch (error) {
        console.error('Error opening WhatsApp Web:', error);

        // Ultimate fallback: try base64 sharing
        shareWithBase64(blob, filename, sharePreviewMessage);
      }
      
      setIsGenerating(false);
    } catch (error) {
      console.error('Error sharing certificate:', error);
      setShareStatus('Failed to share certificate. Please try again.');
      setIsGenerating(false);
    }
  };

  async function createTempImageFile(blob, filename) {
    return new Promise((resolve, reject) => {
      window.resolveLocalFileSystemURL(window.cordova.file.cacheDirectory, function (dirEntry) {
        dirEntry.getFile(filename, { create: true }, function (fileEntry) {
          fileEntry.createWriter(function (fileWriter) {
            fileWriter.onwriteend = () => resolve(fileEntry.nativeURL);
            fileWriter.onerror = (e) => reject(e);
            fileWriter.write(blob);
          });
        }, reject);
      }, reject);
    });
  }

  async function shareImageToWhatsApp(blob, filename, message) {
    try {
      const filePath = await createTempImageFile(blob, filename);

      window.plugins.socialsharing.shareWithOptions({
        message: message,
        files: [filePath]   // ✅ must be array
      }, () => {
        console.log("✅ Shared successfully!");
      }, (err) => {
        console.error("❌ Sharing failed:", err);
      });

    } catch (err) {
      console.error("❌ Could not create temp file:", err);
    }
  }

  // Save file to device using Cordova
  const saveFileToDevice = async (blob, filename) => {
    return new Promise((resolve, reject) => {
      try {
        // Check if we're in a Cordova environment
        if (window.cordova && window.cordova.file) {
          console.log('Using Cordova file API for device save');

          // Convert blob to base64
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const base64Data = reader.result.split(',')[1];

              // Get the appropriate directory (prefer external for user accessibility)
              let directory;
          try {
            // Try to use user-accessible directories first
            if (window.cordova.file.externalRootDirectory) {
              // Try Downloads folder first
              directory = window.cordova.file.externalRootDirectory + 'Download/';
            } else if (window.cordova.file.documentsDirectory) {
              // Documents folder is usually accessible
              directory = window.cordova.file.documentsDirectory;
            } else if (window.cordova.file.externalDataDirectory) {
              // External data directory
              directory = window.cordova.file.externalDataDirectory;
            } else if (window.cordova.file.cacheDirectory) {
              // Cache directory as fallback
              directory = window.cordova.file.cacheDirectory;
            } else {
              // Last resort
              directory = window.cordova.file.dataDirectory;
            }
          } catch (dirError) {
            console.log('Error getting directory, using fallback:', dirError);
            directory = window.cordova.file.dataDirectory;
          }

              console.log('Using directory:', directory);

              // Use the modern Cordova file API
              if (window.resolveLocalFileSystemURL) {
                window.resolveLocalFileSystemURL(directory, (dirEntry) => {
                  dirEntry.getFile(filename, { create: true, exclusive: false }, (fileEntry) => {
                    fileEntry.createWriter((fileWriter) => {
                      fileWriter.onwriteend = () => {
                        const filePath = fileEntry.nativeURL;
                        console.log('File saved successfully to:', filePath);
                        setShareStatus(`Certificate saved to device: ${filename}`);
                        resolve(filePath);
                        // const filePath = fileEntry.toURL();
                        // console.log('File saved successfully to:', filePath);
                        // setShareStatus(`Certificate saved to device: ${filename}`);
                        // resolve(filePath);
                      };

                      fileWriter.onerror = (error) => {
                        console.error('Error writing file:', error);
                        reject(error);
                      };

                      // Write the blob directly (no need for base64 conversion)
                      fileWriter.write(blob);
                    }, (error) => {
                      console.error('Error creating file writer:', error);
                      reject(error);
                    });
                  }, (error) => {
                    console.error('Error creating file:', error);
                    reject(error);
                  });
                }, (err) => {
                  console.error('Error accessing directory:', err);
                  reject(err);
                });
              } else {
                // Fallback to legacy method if modern API not available
                console.log('Using legacy Cordova file API');
                window.cordova.file.writeFile(directory, filename, base64Data, true,
                  (success) => {
                    const filePath = directory + filename;
                    console.log('File saved successfully to:', filePath);
                    setShareStatus(`Certificate saved to device: ${filename}`);
                    resolve(filePath);
                  },
                  (error) => {
                    console.error('Error writing file with legacy API:', error);
                    reject(error);
                  }
                );
              }
            } catch (conversionError) {
              console.error('Error converting blob to base64:', conversionError);
              reject(conversionError);
            }
          };

          reader.onerror = (error) => {
            console.error('Error reading blob:', error);
            reject(error);
          };

          reader.readAsDataURL(blob);
        } else {
          // Not in Cordova environment - use web download
          console.log('Not in Cordova environment, using web download');
          try {
            // Create a download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';

            // Add to DOM, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL after a delay
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 1000);

            // Return a mock file path for consistency
            const mockPath = `file:///downloads/${filename}`;
            setShareStatus(`Certificate downloaded: ${filename}`);
            resolve(mockPath);
          } catch (fallbackError) {
            console.error('Web download fallback failed:', fallbackError);
            reject(fallbackError);
          }
        }
      } catch (error) {
        console.error('Error in saveFileToDevice:', error);
        reject(error);
      }
    });
  };

  function fallbackToTextOnly(message, filename) {
    try {
      const whatsappUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
      window.location.href = whatsappUrl;
      setShareStatus("WhatsApp opened (text only).");
    } catch (urlError) {
      const whatsappWebUrl = `https://wa.me/?text=${encodeURIComponent(
        message + "\n\nCertificate saved to device: " + filename
      )}`;
      window.open(whatsappWebUrl, "_blank");
      setShareStatus("🌐 WhatsApp Web opened! Certificate saved to device.");
    }
  }

  // Share with base64 data for automatic attachment
  const shareWithBase64 = (blob, filename, message) => {
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;

        // Try to share directly to WhatsApp with base64 attachment
        if (/Android/i.test(navigator.userAgent)) {
          // Android: Use WhatsApp intent with base64 data
          const whatsappIntent = `intent://send?text=${encodeURIComponent(message)}&data=${encodeURIComponent(base64Data)}#Intent;package=com.whatsapp;scheme=https;end`;
          window.location.href = whatsappIntent;
          setShareStatus('WhatsApp opened with certificate attachment!');
        } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          // iOS: Use WhatsApp URL scheme with base64 data
          const whatsappUrl = `whatsapp://send?text=${encodeURIComponent(message)}&data=${encodeURIComponent(base64Data)}`;
          window.location.href = whatsappUrl;
          setShareStatus('WhatsApp opened with certificate attachment!');
        } else {
          // Desktop: Try to open WhatsApp Web with base64 data
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message + '\n\n📎 Certificate image attached as base64 data')}`;
          window.open(whatsappUrl, '_blank');
          setShareStatus('WhatsApp Web opened! Image data included.');
        }
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Base64 sharing failed:', error);
      // Ultimate fallback: download and open WhatsApp
      downloadBlob(blob, filename);
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      setShareStatus('Image downloaded! WhatsApp opened.');
    }
  };

  const handleDownloadImage = async () => {
    console.log("img", certificateImage)
    try {
      if (certificateImage) {
        setIsGenerating(true);
        setShareStatus('Preparing download...');

        // Convert the certificate image to blob for proper handling
        const response = await fetch(certificateImage);
        const blob = await response.blob();

        // Use the enhanced downloadBlob function with iOS support
        let filename = buildFilename();
        
        // Ensure filename has .png extension
        if (!filename.toLowerCase().endsWith('.png')) {
          filename = filename + '.png';
        }

        console.log('Using enhanced downloadBlob for Certificate image, filename:', filename);
        downloadBlob(blob, filename);

        setIsGenerating(false);
      } else {
        setShareStatus('No certificate image available for download.');
        setIsGenerating(false);
      }
    } catch (error) {
      console.error('Error downloading certificate:', error);
      setShareStatus('Download failed. Please try again.');
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsGenerating(true);
      setShareStatus('Generating PDF...');
      const filename = buildFilename();
      
      if (!certificateImage) {
        throw new Error("No certificate image available");
      }

      // Fetch the certificate blob
      const response = await fetch(certificateImage);
      const blob = await response.blob();

      // Convert blob → base64 (for jsPDF addImage)
      const base64 = await blobToBase64(blob);

      const img = new Image();
      img.src = base64;

      img.onload = () => {
        const pdf = new jsPDF({
          orientation: img.width > img.height ? "l" : "p",
          unit: "px",
          format: [img.width, img.height],
        });

        pdf.addImage(img, "PNG", 0, 0, img.width, img.height);

        // Generate a PDF blob
        const pdfBlob = pdf.output("blob");
        const pdfFile = new Blob([pdfBlob], { type: "application/pdf" });

        // Use the enhanced downloadBlob function with iOS support
        const pdfFilename = filename.replace(/\.(png|jpg|jpeg)$/i, ".pdf");
        console.log('Using enhanced downloadBlob for Certificate PDF, filename:', pdfFilename);
        downloadBlob(pdfFile, pdfFilename);
      };
    } catch (error) {
      console.error('Error generating PDF:', error);
      setShareStatus('PDF generation failed. Please try again.');
      setTimeout(() => setShareStatus(''), 3000);
    } finally {
      setIsGenerating(false);
    }
  };

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  }

  // generateCertificateImage function removed - now using API certificate image

  const downloadBlob = (blob, filename) => {
    try {
      console.log('Starting download for:', filename, 'Blob size:', blob.size);

      // Enhanced mobile download handling with Cordova support
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOSDevice = isIOS();
      const isSafariBrowser = isSafari();
      
      console.log('Device type - Mobile:', isMobile, 'iOS:', isIOSDevice, 'Safari:', isSafariBrowser, 'Cordova available:', !!window.cordova);

      // Handle iOS devices specifically
      if (isIOSDevice) {
        console.log('iOS device detected, using iOS-specific download handler');
        
        // Use specific PDF handler for PDF files on iOS
        if (filename.toLowerCase().endsWith('.pdf')) {
          console.log('iOS PDF download detected, using specialized PDF handler');
          handleIOSPDFDownload(blob, filename);
        } else {
          handleIOSDownload(blob, filename);
        }
        return;
      }

      // Try Cordova file handling first if available
      if (window.cordova && window.cordova.file) {
        console.log('Attempting Cordova file save...');
        setShareStatus('Saving certificate to device...');

        saveFileToDevice(blob, filename).then((filePath) => {
          console.log('Certificate saved successfully to:', filePath);
          setShareStatus(`Certificate saved to device: ${filename}`);
        }).catch((error) => {
          console.error('Cordova save failed, falling back to regular download:', error);
          setShareStatus('Device save failed, using download...');
          fallbackDownload(blob, filename);
        });
        return;
      }

      // Try Web Share API for mobile devices
      if (isMobile && navigator.share && navigator.canShare) {
        console.log('Attempting Web Share API...');
        const file = new File([blob], filename, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          navigator.share({
            files: [file],
            title: 'Peacekeeper Certificate',
            text: 'Download your certificate image'
          }).then(() => {
            setShareStatus('Certificate shared successfully!');
          }).catch((shareError) => {
            console.log('Web Share failed, falling back to download:', shareError);
            setShareStatus('Sharing failed, downloading instead...');
            fallbackDownload(blob, filename);
          });
          return;
        }
      }

      // For mobile devices without Web Share API, try to open in new tab
      if (isMobile && !navigator.share) {
        console.log('Mobile device without Web Share API, opening in new tab...');
        setShareStatus('Opening certificate in new tab for mobile download...');

        try {
          const url = URL.createObjectURL(blob);
          const newWindow = window.open(url, '_blank');

          if (newWindow) {
            // Give user instructions
            setShareStatus('Certificate opened in new tab. Long press and save image on mobile.');

            // Clean up URL after a delay
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 5000);
          } else {
            // If popup blocked, fall back to regular download
            fallbackDownload(blob, filename);
          }
        } catch (mobileError) {
          console.error('Mobile tab opening failed, falling back to download:', mobileError);
          fallbackDownload(blob, filename);
        }
        return;
      }

      // Regular download for desktop or when other methods are not available
      console.log('Using fallback download method...');
      setShareStatus('Downloading certificate...');
      fallbackDownload(blob, filename);

    } catch (error) {
      console.error('Error:', error);
      setShareStatus('Download failed. Please try again.');
      // Ultimate fallback
      fallbackDownload(blob, filename);
    }
  };

  const downloadPDF = (blob, filename) => {
    try {
      const img = new Image();
      img.onload = () => {
        // Create jsPDF with same orientation/size as the image
        const pdf = new jsPDF({
          orientation: img.width > img.height ? "l" : "p",
          unit: "px",
          format: [img.width, img.height]
        });

        // Draw the image into the PDF
        pdf.addImage(img, "PNG", 0, 0, img.width, img.height);

        // Save the PDF
        pdf.save(filename.replace(".png", ".pdf"));

        setShareStatus("PDF downloaded successfully!");
      };

      img.src = URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error in downloadPDF:", error);
      setShareStatus("PDF download failed. Please try again.");
    }
  };

  const fallbackDownload = (blob, filename) => {
    try {
      console.log('Using fallback download for:', filename);

      // Ensure the blob is valid
      if (!blob || blob.size === 0) {
        console.error('Invalid blob for download:', blob);
        setShareStatus('Download failed: Invalid file data');
        return;
      }

      // Check if we're on a mobile device
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        console.log('Mobile device detected, using mobile-friendly download...');
        setShareStatus('Opening certificate for mobile download...');

        // For mobile, open in new tab and let user save manually
        const url = URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');

        if (newWindow) {
          setShareStatus('Certificate opened in new tab. Long press and save image on mobile.');

          // Clean up URL after a longer delay for mobile
          setTimeout(() => {
            URL.revokeObjectURL(url);
          }, 10000); // 10 seconds for mobile users
        } else {
          // If popup blocked, show error message
          setShareStatus('Download failed. Please check your browser settings and try again.');
        }
        return;
      }

      // Desktop download logic
      console.log('Desktop device, using standard download...');

      // Check if download attribute is supported
      if (isDownloadSupported()) {
        // Create a download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        // Add to DOM, click, and remove
        document.body.appendChild(link);

        // Trigger download
        link.click();

        // Clean up
        document.body.removeChild(link);

        // Clean up the URL after a delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);

        console.log('Fallback download completed for:', filename);
        setShareStatus(`Certificate downloaded: ${filename}`);
      } else {
        // Download attribute not supported, open in new tab
        console.log('Download attribute not supported, opening in new tab...');
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setShareStatus('Certificate opened in new tab. Right-click and save as...');
      }

    } catch (error) {
      console.error('Error in fallbackDownload:', error);
      setShareStatus('Download failed. Please try again.');

      // Try one more time with a different approach
      try {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setShareStatus('Certificate opened in new tab');
      } catch (finalError) {
        console.error('Final download attempt failed:', finalError);
        setShareStatus('Download completely failed. Please check your browser settings.');
      }
    }
  };

  // Profile image fetching removed - not needed for API certificate

  // Fetch certificate from API
  useEffect(() => {
    const fetchCertificate = async () => {
      if (user?.userid) {
        setCertificateLoading(true);
        setCertificateError(null);
        try {
          // Use getBlobAPI to get the certificate image as blob
          const response = await apiServices.getBlobAPI(`certificate/${user.userid}`);

          if (response.data) {
            // Create blob URL from the response
            const blob = new Blob([response.data], { type: 'image/png' });
            const imageUrl = URL.createObjectURL(blob);
            setCertificateImage(imageUrl);
            console.log('Certificate fetched successfully:', imageUrl);
          }
        } catch (error) {
          console.error('Error fetching certificate:', error);

          // Check if it's a 404 error (certificate not found)
          if (error.response?.status === 404) {
            console.log('Certificate not found (404), attempting to generate automatically...');
            console.log('Error details:', error.response);
            setShareStatus('Certificate not found. Generating automatically...');

            // Automatically call the generate API
            try {
              setIsAutoGenerating(true);
              const updateResponse = await apiServices.putAPI(`update-certificate/${userData.idNumber}`, {
                forceRegenerate: true
              });

              console.log('Certificate generation response:', updateResponse);

              if (updateResponse.status === 200) {
                setShareStatus('Certificate generated successfully! Fetching...');

                // Wait a moment for the certificate to be generated, then retry fetching
                setTimeout(async () => {
                  try {
                    const retryResponse = await apiServices.getBlobAPI(`certificate/${user.userid}`);

                    if (retryResponse.data) {
                      const blob = new Blob([retryResponse.data], { type: 'image/png' });
                      const imageUrl = URL.createObjectURL(blob);
                      setCertificateImage(imageUrl);
                      setShareStatus('Certificate generated and loaded successfully!');
                      console.log('Certificate fetched after generation:', imageUrl);
                    }
                  } catch (retryError) {
                    console.error('Error fetching certificate after generation:', retryError);
                    setCertificateError('Certificate was generated but failed to load. Please refresh the page.');
                  } finally {
                    setIsAutoGenerating(false);
                  }
                }, 2000); // Wait 2 seconds for generation to complete

              } else {
                throw new Error('Failed to generate certificate');
              }
            } catch (updateError) {
              console.error('Error generating certificate:', updateError);
              setCertificateError('Failed to generate certificate. Please try again later.');
              setIsAutoGenerating(false);
            }
          } else {
            // It's a different error, show the original error message
            setCertificateError('Failed to load certificate. Please try again later.');
          }
        } finally {
          setCertificateLoading(false);
        }
      }
    };

    fetchCertificate();
  }, [user?.userid]);

  // Listen for certificate updates from EditProfile.js
  useEffect(() => {
    const handleCertificateUpdate = async (event) => {
      console.log('Certificate update event received:', event.detail);

      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing certificate image after profile update...');
        setShareStatus('Certificate updated! Refreshing with new profile information...');

        // Wait a moment for the backend to process, then refresh
        setTimeout(async () => {
          try {
            const response = await apiServices.getBlobAPI(`certificate/${user.userid}`);

            if (response.data) {
              // Clean up old blob URL to prevent memory leaks
              if (certificateImage && certificateImage.startsWith('blob:')) {
                URL.revokeObjectURL(certificateImage);
              }

              // Create new blob URL from the response
              const blob = new Blob([response.data], { type: 'image/png' });
              const imageUrl = URL.createObjectURL(blob);
              setCertificateImage(imageUrl);
              setShareStatus('Certificate refreshed successfully with updated profile information!');
              console.log('Certificate refreshed after profile update:', imageUrl);

              // Clear success message after 3 seconds
              setTimeout(() => {
                setShareStatus('');
              }, 3000);
            }
          } catch (error) {
            console.error('Error refreshing certificate after profile update:', error);
            setShareStatus('Certificate refresh failed. Please refresh the page manually.');
          }
        }, 2000); // Wait 2 seconds for backend processing
      }
    };

    // Add event listener
    window.addEventListener('certificateUpdated', handleCertificateUpdate);

    // Cleanup event listener
    return () => {
      window.removeEventListener('certificateUpdated', handleCertificateUpdate);
    };
  }, [user?.userid, certificateImage]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (certificateImage && certificateImage.startsWith('blob:')) {
        URL.revokeObjectURL(certificateImage);
      }
    };
  }, [certificateImage]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading certificate...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Please log in to view your certificate.</Typography>
      </Box>
    );
  }

  return (
    <>

      {/* Information Notice */}
      <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Download and share your official <strong>I Am Peacekeeper Certificate</strong>. <br />

          Your Certificate carries your unique QR code and referral link, enabling you to invite others into the <strong>I
            Am Peacekeeper Movement</strong>.
        </Typography>
      </Box>

      {/* Certificate Display Section */}
      {certificateLoading || isAutoGenerating ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={60} />
          <Typography sx={{ ml: 2 }}>
            {isAutoGenerating ? 'Generating certificate automatically...' : 'Loading your certificate...'}
          </Typography>
        </Box>
      ) : certificateError ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4, gap: 2 }}>
          <Typography color="error" variant="h6">{certificateError}</Typography>
          {!isAutoGenerating && (
            <Button
              variant="contained"
              onClick={handleGenerateCertificate}
              disabled={isGenerating}
              sx={{
                bgcolor: '#4caf50',
                '&:hover': { bgcolor: '#45a049' }
              }}
            >
              {isGenerating ? 'Generating...' : 'Generate Certificate'}
            </Button>
          )}
          {isAutoGenerating && (
            <Typography color="info" variant="body2">
              Certificate generation is in progress. Please wait...
            </Typography>
          )}
        </Box>
      ) : certificateImage ? (
        <Paper elevation={10} sx={{ p: 2, textAlign: 'center' }}>
          <img
            src={certificateImage}
            alt="I Am Peacekeeper Certificate"
            className="certificate-image"
          />
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          {isAutoGenerating ? (
            <>
              <CircularProgress size={40} />
              <Typography sx={{ ml: 2 }}>Generating certificate automatically...</Typography>
            </>
          ) : (
            <>
              <Typography color="text.secondary">No certificate available</Typography>
              <Button
                variant="contained"
                onClick={handleGenerateCertificate}
                disabled={isGenerating}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#45a049' }
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate Certificate'}
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Commented out HTML Certificate Generation Code - Using API instead */}
      {/* 
      <Paper
        className="certificate-container"
        elevation={10}
        ref={certificateRef}
        sx={{
          '@media print': {
            boxShadow: 'none',
            margin: 0,
            padding: 0,
            backgroundColor: 'white',
            '& .certificate-container': {
              backgroundColor: 'white'
            }
          }
        }}
      >
        
        Top Logo
        <Grid container spacing={1}>
          <Grid size={{ xs: 12 }} sx={{ textAlign: 'right', pr: 2, pt: 2 }}>
            <img
              src={imgUrl.peaceMovImg}
              alt="Peacekeeper Logo"
              style={{ width: 90 }}
            />
          </Grid>
        </Grid>

        Avatar + Title
        <Grid container spacing={2} sx={{ px: 4 }} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <Avatar
              src={profileImage || userData.photo}
              alt={userData.name}
              sx={{
                width: 200,
                height: 200,
                border: '4px solid white',
                marginTop: '-150px'
              }}
            >
              {imageLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </Box>
              ) : !profileImage && !userData.photo ? (
                <Typography variant="h4" color="text.secondary">
                  {userData.name?.charAt(0)?.toUpperCase() || 'U'}
                </Typography>
              ) : null}
            </Avatar>
          </Grid>

          <Grid
            size={{ xs: 12, md: 7 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'end',
              alignItems: 'flex-end',
              pt: 0
            }}
            className="peaceTitle"
          >
            <img src={imgUrl.iamPeaceTextImg} className='w-100 peaceTextImg'></img>
          </Grid>
        </Grid>

        Details + QR + Certificate
        <Grid container spacing={3} sx={{ mt: 0, px: 4 }} className="certificateNameContainer">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '20px',
                color: '#fff',
                display: 'inline-block',
                marginBottom: '2px',
                borderBottom: "1px solid #fff"
              }}
            >
              {userData.name}
            </Typography>
            <Typography sx={{ color: '#000' }}><strong>Country:</strong> {userData.country}</Typography>
            <Typography sx={{ color: '#000' }}><strong>ID No.:</strong> {userData.idNumber}</Typography>
            {showMobile && (
              <Typography sx={{ color: '#000' }}><strong>Mobile:</strong> {userData.mobile}</Typography>
            )}
            {showEmail && (
              <Typography sx={{ color: '#000' }}><strong>E-mail:</strong> {userData.email}</Typography>
            )}

            Additional user details
            {user?.date_of_birth && (
              <Typography sx={{ color: '#000' }}>
                <strong>Date of Birth:</strong> {new Date(user.date_of_birth).toLocaleDateString()}
              </Typography>
            )}
            {user?.hasPassword && (
              <Typography sx={{ color: '#000' }}>
                <strong>Status:</strong> <span style={{ color: '#28a745' }}>✓ Verified</span>
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  color: '#FFD700',
                  fontWeight: 'bold',
                  fontSize: '30px',
                  lineHeight: '40px'
                }}
              >
                CERTIFICATE
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#000', fontWeight: 600 }}>
                <Link
                  href="https://www.i-am-peacekeeper.com/"
                  underline="hover"
                  sx={{ color: '#000' }}
                >
                  www.i-am-peacekeeper.com
                </Link>
              </Typography>
            </Box>
          </Grid>

          QR Code
          <Grid size={{ xs: 12, md: 6 }} sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            overflow: 'hidden',
            pl: 2
          }} className="QrSection">
            <Box sx={{
              p: 1,
              bgcolor: '#f8f9fa',
              borderRadius: 2,
              textAlign: 'center',
              width: '100%',
              maxWidth: '200px'
            }}>
              <QRCode
                value={qrCodeData}
                size={150}
                logoImage={peacemakerImg}
                logoWidth={40}
                logoHeight={40}
                logoOpacity={1}
                logoPadding={3}
                logoPaddingStyle="circle"
                removeQrCodeTail={true}
                qrStyle="dots"
                eyeRadius={[3, 3, 3]}
                eyeColor={["#214F7D", "#214F7D", "#214F7D"]}
                backgroundOptions={{ color: "#ffffff" }}
                logoOptions={{
                  backgroundColor: "#ffffff",
                  padding: 6,
                  borderRadius: 6,
                  borderWidth: 2,
                  borderColor: "#214F7D",
                }}
              />
            </Box>

            <Typography sx={{ fontSize: '12px', color: '#fff', mt: 0, fontWeight: 500 }}>
              Scan to register as Peacekeeper
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      */}

      {/* Contact info toggle removed - not needed for API certificate */}
      {/* Generate Certificate Button */}
      {/* {!isAutoGenerating && (
        <Box sx={{ 
          mt: 3, 
          p: 1, 
          bgcolor: 'grey.50', 
          borderRadius: 2,
          justifyContent: 'center',
          maxWidth: '600px',
          mx: 'auto',
          gap: 1
        }}>
          <Button
            variant="outlined"
            onClick={handleGenerateCertificate}
            disabled={isGenerating || certificateLoading}
            sx={{
              borderColor: '#4caf50',
              color: '#4caf50',
              '&:hover': { 
                borderColor: '#45a049',
                backgroundColor: 'rgba(76, 175, 80, 0.04)'
              },
              minWidth: '200px'
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate New Certificate'}
          </Button>
        </Box>
      )} */}

      {/* Download Buttons */}
      <Box sx={{
        mt: 2,
        mb: 3,
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadImage}
          disabled={isGenerating || !certificateImage || certificateLoading || isAutoGenerating}
          sx={{
            bgcolor: '#1976d2',
            '&:hover': { bgcolor: '#1565c0' },
            minWidth: '140px'
          }}
        >
          {isGenerating ? 'Preparing...' : 'Download PNG'}
        </Button>

        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadPDF}
          disabled={isGenerating || !certificateImage || certificateLoading || isAutoGenerating}
          sx={{
            bgcolor: '#d32f2f',
            '&:hover': { bgcolor: '#c62828' },
            minWidth: '140px'
          }}
        >
          {isGenerating ? 'Preparing...' : 'Download PDF'}
        </Button>

        <Button
          variant="contained"
          startIcon={<ShareIcon />}
          onClick={handleShareNoDownload}
          disabled={isGenerating || !certificateImage || certificateLoading || isAutoGenerating}
          sx={{
            bgcolor: '#25d366',
            '&:hover': { bgcolor: '#128c7e' },
            minWidth: '140px'
          }}
        >
          {isGenerating ? 'Preparing...' : 'Preview & Share'}
        </Button>
      </Box>

      {/* Status and Instructions Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        marginTop: '20px'
      }}>
        {/* Auto-generation Status */}
        {isAutoGenerating && (
          <Chip
            label="Generating certificate "
            color="info"
            size="small"
            sx={{ mb: 1 }}
            icon={<CircularProgress size={16} />}
          />
        )}

        {/* Share Status Indicator */}
        {shareStatus && (
          <Chip
            label={shareStatus}
            color="success"
            size="small"
            sx={{ mb: 1 }}
            onDelete={() => setShareStatus('')}
          />
        )}
      </Box>

      {/* Menu removed - simplified UI with direct buttons */}

      {/* Share Preview Modal */}
      <Dialog 
        open={showSharePreview} 
        onClose={() => setShowSharePreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Certificate Preview
          <IconButton
            aria-label="close"
            onClick={() => setShowSharePreview(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          dividers
          sx={{
            maxHeight: '70vh',
            overflowY: 'auto'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Message Preview:
            </Typography>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
                fontFamily: 'DIN Pro Regular, Arial, sans-serif',
                fontSize: '0.9rem'
              }}
            >
              {sharePreviewMessage}
            </Paper>
          </Box>
          {certificateImage && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Certificate Attachment:
              </Typography>
              <img 
                src={certificateImage} 
                alt="Certificate Preview" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '300px', 
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setShowSharePreview(false)} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendShare}
            variant="contained"
            startIcon={<ShareIcon />}
            sx={{
              bgcolor: '#25d366',
              '&:hover': { bgcolor: '#128c7e' },
            }}
          >
            Share Via
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Certificate;
